/**
 * Maps a backend API error (from axios) into a UX decision for modal forms.
 *
 * Backend error shape (see flexibook-api/src/utils/errorResponse.js):
 *   { error: { message, code?, details? } }
 *
 * Returns one of:
 *   { handled: true }                               // already surfaced inline via setError
 *   { handled: false, kind: "auth" }                // let global 401 flow handle it
 *   { handled: false, kind: "verify", banner }      // email not verified
 *   { handled: false, kind: "generic", banner }     // show banner at top of form
 *
 * Options:
 *   setError      — react-hook-form setError. If omitted, field-level mapping is skipped.
 *   fieldMap      — map backend field name → form field name (e.g. { phoneNumber: "phone" }).
 *   setFieldError — optional (field, message) => boolean callback for fields the
 *                   caller manages outside react-hook-form (uncontrolled fields
 *                   where setError would no-op). Return true when the callback
 *                   displayed the message itself; the mapper then skips setError
 *                   for that field. Return false (or omit handling the field) to
 *                   fall back to setError. Kept generic on purpose — the mapper
 *                   never hardcodes field names; the caller's callback decides.
 */
export function mapApiError(err, { setError, fieldMap, setFieldError } = {}) {
  const response = err?.response;
  const apiError = response?.data?.error;
  const code = apiError?.code;
  const message = apiError?.message;
  const details = apiError?.details;
  const status = response?.status;

  // 1. Field-attributable errors → inline on the offending field
  if (setError) {
    if (code === "CLIENT_ALREADY_EXISTS") {
      setError("email", {
        type: "server",
        message: "A client with this email already exists.",
      });
      return { handled: true };
    }

    if (code === "SLOT_CONFLICT") {
      setError("startTime", {
        type: "server",
        message: "This time is no longer available. Please pick another.",
      });
      return { handled: true };
    }

    // Any error carrying details[].field → route each to its field. Covers
    // VALIDATION_ERROR (400) and field-attributed conflicts like
    // AVAILABILITY_LOCATION_CONFLICT (409, duplicate fixed location →
    // baseAddress). Used by public booking today, and by the internal
    // availability/service save endpoints now that they emit this shape.
    if (Array.isArray(details) && details.length > 0) {
      details.forEach(({ field, message: fieldMessage }) => {
        if (!field || !fieldMessage) return;
        const handledByCaller =
          typeof setFieldError === "function" &&
          setFieldError(field, fieldMessage);
        if (handledByCaller) return;
        const uiField = fieldMap?.[field] ?? field;
        setError(uiField, { type: "server", message: fieldMessage });
      });
      return { handled: true };
    }
  }

  // 2. Auth — let the global axios interceptor handle redirect.
  if (status === 401 || code === "UNAUTHORIZED") {
    return { handled: false, kind: "auth" };
  }

  // 3. Email verification — the page should toast + link to verify flow.
  if (status === 403 || code === "EMAIL_NOT_VERIFIED") {
    return {
      handled: false,
      kind: "verify",
      banner: {
        title: "Verify your email",
        message:
          "You need to verify your email address before you can make changes.",
      },
    };
  }

  // 4. Network error (no response at all)
  if (!response) {
    return {
      handled: false,
      kind: "network",
      banner: {
        title: "Can't reach the server",
        message:
          "Check your internet connection and try again.",
      },
    };
  }

  // 5. 5xx — never leak raw backend messages.
  if (status >= 500) {
    return {
      handled: false,
      kind: "server",
      banner: {
        title: "Something went wrong",
        message:
          "Please try again in a moment. If this keeps happening, contact support.",
      },
    };
  }

  // 6. 4xx fallback — backend message is generally safe to surface.
  return {
    handled: false,
    kind: "generic",
    banner: {
      title: "Couldn't save",
      message: message || "Please review the form and try again.",
    },
  };
}
